import React, { useEffect, useRef, useState } from "react";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowEditCompleteParams,
  DataTableRowReorderParams,
} from "primereact/datatable";
import { Button } from "primereact/button";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentGroupLayout,
  DashboardComponentJustify} from "api/model/dashboard/DashboardComponentGroup";
import { Column, ColumnEditorOptions } from "primereact/column";

import "./DashboardReportTabEditor.scss";
import { formatTime } from "utils/time-utils";
import { DashboardLayoutType, DashboardComponentModel } from "api/model/dashboard/DashboardComponent";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";

import Editor, { Monaco } from "@monaco-editor/react";
import { schema } from "./schemas/componentSchema";
import { Dropdown } from "primereact/dropdown";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";

import { InputNumber } from "primereact/inputnumber";

const DashboardTabsSubForm = ({
  dashboardTabs,
  onChange,
}: {
  dashboardTabs: Array<DashboardTabModel>;
  onChange: (o: Array<DashboardTabModel>) => void;
}) => {
  const [expandedTabRows, setExpandedTabRows] = useState<any[] | DataTableExpandedRows | undefined>(
    [...dashboardTabs]
  );

  const onRowEditComplete = (e: DataTableRowEditCompleteParams) => {
    replaceTab(e.newData, e.index);
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={e => options.editorCallback!!(e.target.value)}
      />
    );
  };

  const replaceTab = (givenTab: DashboardTabModel, index: number) => {
    const newTabs = [...dashboardTabs];
    newTabs[index] = givenTab;

    onChange(newTabs);

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return newTabs;
      }

      if (
        !givenTab.componentGroups ||
        (givenTab.componentGroups && givenTab.componentGroups.length === 0)
      ) {
        return oldExpands.filter(dashboard => dashboard !== givenTab);
      }

      if (!oldExpands.some(dashboard => dashboard === givenTab)) {
        return [...oldExpands, givenTab];
      }

      return oldExpands.map(dashboard => (dashboard === givenTab ? givenTab : dashboard));
    });
  };

  const onAddTabClick = () => {
    const tab: DashboardTabModel = {
      title: formatTime(new Date()),
      componentGroups: [],
    };

    onChange([...dashboardTabs, tab]);
  };

  const onRemoveTabClick = (rowData: DashboardTabModel) => {
    onChange(
      dashboardTabs.filter(function (el) {
        return el !== rowData;
      })
    );
  };

  const onRowReorder = (e: DataTableRowReorderParams) => {
    onChange([...e.value]);
  };

  const confirmTabDelete = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    rowData: DashboardTabModel
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Do you want to delete this tab?",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveTabClick(rowData),
    });
  };

  const onAddGroupClick = (rowData: DashboardTabModel) => {
    const tempTab: DashboardTabModel = {
      ...rowData,
      componentGroups: [
        ...rowData.componentGroups,
        { components: [], layout: { type: DashboardLayoutType.VERTICAL } },
      ],
    };

    const tempTabs = dashboardTabs
      .slice()
      .map(dashboard => (dashboard === rowData ? tempTab : dashboard));

    onChange(tempTabs);

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return [tempTab];
      }

      if (!oldExpands.some(dashboard => dashboard === rowData)) {
        return [...oldExpands, tempTab];
      }

      return oldExpands.map(dashboard => (dashboard === rowData ? tempTab : dashboard));
    });
  };

  const addButton = (rowData: DashboardTabModel) => (
    <Button
      icon={"pi pi-plus"}
      label={"Group"}
      className={"p-button-sm p-button-success"}
      onClick={() => {
        onAddGroupClick(rowData);
      }}
    />
  );
  const removeButton = (rowData: DashboardTabModel) => (
    <Button
      icon={"pi pi-trash"}
      className={"p-button-sm p-button-danger"}
      onClick={event => confirmTabDelete(event, rowData)}
    />
  );

  return (
    <div style={{ height: "500px", overflowY: "auto" }} className={"flex flex-column gap-1"}>
      <ConfirmPopup />
      <div>
        <Button
          icon={"pi pi-plus"}
          label={"Tab"}
          className={"mt-2 p-button-success p-button-sm"}
          onClick={() => onAddTabClick()}
        />
      </div>
      <DataTable
        value={dashboardTabs}
        size={"small"}
        reorderableRows
        onRowReorder={onRowReorder}
        editMode="row"
        expandedRows={expandedTabRows}
        onRowEditComplete={onRowEditComplete}
        onRowToggle={e => setExpandedTabRows(e.data)}
        rowExpansionTemplate={(data, options) => {
          return (
            <TabExpansionTemplate
              tab={{
                title: data.title,
                componentGroups: data.componentGroups,
                reportOptions: data.reportOptions,
              }}
              onChange={replaceTab}
              tabIndex={options.index}
            />
          );
        }}
      >
        <Column
          expander={rowData => rowData.componentGroups?.length > 0}
          style={{ width: "3em" }}
        />
        <Column field={"title"} header={"Tabs"} editor={options => textEditor(options)} />
        <Column
          rowEditor
          headerStyle={{ width: "0", minWidth: "6rem" }}
          bodyStyle={{ textAlign: "center" }}
        ></Column>
        <Column style={{ width: "0" }} body={addButton} />
        <Column style={{ width: "0" }} body={removeButton} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

type TabExpansionTemplateType = {
  tab: DashboardTabModel;
  onChange: (tab: DashboardTabModel, index: number) => void;
  tabIndex: number;
};

interface GroupWithTitle extends DashboardComponentGroup {
  title: string;
}

const TabExpansionTemplate = (props: TabExpansionTemplateType) => {
  const tempGroups: Array<GroupWithTitle> = props.tab.componentGroups.map((ob, index) => ({
    ...ob,
    title: `Component group ${index + 1}`,
  }));
  const [expandedGroupRows, setExpandedGroupRows] = useState<
    any[] | DataTableExpandedRows | undefined
  >([...tempGroups]);
  const groupChange = (group: DashboardComponentGroup, index: number) => {
    const newGroups = [...props.tab.componentGroups];
    newGroups[index] = group;

    props.onChange({ ...props.tab, componentGroups: newGroups }, props.tabIndex);

    setExpandedGroupRows(prevState => {
      if (!Array.isArray(prevState)) {
        return newGroups;
      }

      if (!group.components || (group.components && group.components.length === 0)) {
        return prevState.filter(g => g !== group);
      }

      if (!prevState.some(group => group.index === index)) {
        return [...prevState, group];
      }

      return prevState.map(ob => (ob.index === index ? group : ob));
    });
  };

  const onAddComponentClick = (rowData: GroupWithTitle, index: number) => {
    const comp = Object.freeze({ title: "", type: "", config: {} });
    const group = { ...rowData, components: [...rowData.components, comp] };
    const groupNew = [...props.tab.componentGroups];
    groupNew[index] = { ...groupNew[index], components: group.components };

    props.onChange({ ...props.tab, componentGroups: groupNew }, props.tabIndex);

    setExpandedGroupRows(oldExpands => [
      ...((oldExpands as any[]) ?? []).filter(oldGroup => oldGroup.title !== rowData.title),
      group,
    ]);
  };

  const onRemoveGroupClick = (index: number) => {
    const groupTemp = [...props.tab.componentGroups];
    groupTemp.splice(index, 1);

    props.onChange(
      {
        ...props.tab,
        componentGroups: groupTemp,
      },
      props.tabIndex
    );
  };

  const onRowReorder = (e: DataTableRowReorderParams) => {
    setExpandedGroupRows([]);
    props.onChange(
      {
        ...props.tab,
        componentGroups: e.value,
      },
      props.tabIndex
    );
  };

  const confirmGroupDelete = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Do you want to delete this group?",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveGroupClick(index),
    });
  };

  const editButton = (rowData: DashboardComponentGroup, index: number) => {
    const [dialog, setDialog] = useState(false);

    const onGroupLayoutChange = (newLayout: DashboardComponentGroupLayout) => {
      groupChange({ ...rowData, layout: newLayout }, index);
    };

    return (
      <>
        <Button icon={"pi pi-cog"} className={"p-button-sm"} onClick={() => setDialog(true)} />
        <Dialog
          visible={dialog}
          onHide={() => setDialog(false)}
          header={`Component Group ${index + 1} Configuration`}
          style={{ width: "min(100%, 1000px)" }}
        >
          <GroupLayoutConfiguration
            layout={rowData.layout}
            onChange={onGroupLayoutChange}
            closeDialog={() => setDialog(false)}
          />
        </Dialog>
      </>
    );
  };

  const addButton = (rowData: GroupWithTitle, index: number) => (
    <Button
      icon="pi pi-plus"
      label="Component"
      className="p-button-sm p-button-success"
      onClick={() => {
        onAddComponentClick(rowData, index);
      }}
    />
  );
  const removeButton = (index: number) => (
    <Button
      icon={"pi pi-trash"}
      className={"p-button-sm p-button-danger"}
      onClick={event => confirmGroupDelete(event, index)}
    />
  );

  return (
    <div className="group-table">
      <DataTable
        value={tempGroups}
        reorderableRows
        onRowReorder={onRowReorder}
        size={"small"}
        editMode="row"
        expandedRows={expandedGroupRows}
        onRowToggle={e => setExpandedGroupRows(e.data)}
        rowExpansionTemplate={(data, options) => (
          <GroupExpansionTemplate group={data} groupIndex={options.index} onChange={groupChange} />
        )}
      >
        <Column expander={rowData => rowData.components?.length > 0} style={{ width: "3em" }} />
        <Column
          field="title"
          header={"Groups"}
          body={(data, options) => `Component group ${options.rowIndex + 1}`}
        ></Column>
        <Column
          style={{ width: "0" }}
          body={(data, options) => addButton(data, options.rowIndex)}
        />
        <Column
          style={{ width: "0" }}
          body={(rowData, options) => editButton(rowData, options.rowIndex)}
        />
        <Column style={{ width: "0" }} body={(data, options) => removeButton(options.rowIndex)} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

type GroupExpansionTemplateType = {
  group: DashboardComponentGroup;
  onChange: (group: DashboardComponentGroup, index: number) => void;
  groupIndex: number;
};

const GroupExpansionTemplate = (props: GroupExpansionTemplateType) => {
  const onRowReorder = (e: DataTableRowReorderParams) => {
    const components = [...e.value];

    props.onChange({ ...props.group, components: components }, props.groupIndex);
  };

  const onRemoveComponentClick = (rowData: DashboardComponentModel, index: number) => {
    const componentsTemp = [...props.group.components];
    componentsTemp.splice(index, 1);

    props.onChange({ ...props.group, components: componentsTemp }, props.groupIndex);
  };

  const confirmComponentDelete = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    rowData: DashboardComponentModel,
    index: number
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Do you want to delete this component?",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveComponentClick(rowData, index),
    });
  };

  const editButton = (rowData: DashboardComponentModel, index: number) => {
    const [dialog, setDialog] = useState(false);

    const onComponentChange = (newComponent: DashboardComponentModel) => {
      const components = [...props.group.components];
      components[index] = newComponent;

      props.onChange({ ...props.group, components: components }, props.groupIndex);
    };

    return (
      <>
        <Button icon={"pi pi-cog"} className={"p-button-sm"} onClick={() => setDialog(true)} />
        <Dialog
          visible={dialog}
          onHide={() => setDialog(false)}
          header={`${rowData.title || ""} Component Configuration`}
          style={{ width: "min(100%, 1000px)" }}
        >
          <ComponentConfiguration
            component={rowData}
            onChange={onComponentChange}
            closeDialog={() => setDialog(false)}
          />
        </Dialog>
      </>
    );
  };

  const removeButton = (rowData: DashboardComponentModel, index: number) => (
    <Button
      icon={"pi pi-trash"}
      className={"p-button-sm p-button-danger"}
      onClick={event => confirmComponentDelete(event, rowData, index)}
    />
  );

  return (
    <div className="group-table">
      <DataTable
        value={props.group.components}
        size={"small"}
        editMode="row"
        reorderableRows
        onRowReorder={onRowReorder}
      >
        <Column
          field="title"
          header={`Components`}
          body={data => (data.title && data.title !== "" ? data.title : "-")}
        ></Column>
        <Column field="type" header={`Type`}></Column>
        <Column
          style={{ width: "0" }}
          body={(rowData, options) => editButton(rowData, options.rowIndex)}
        />
        <Column
          style={{ width: "0" }}
          body={(rowData, options) => removeButton(rowData, options.rowIndex)}
        />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

type ComponentConfigurationType = {
  component: DashboardComponentModel;
  onChange: (newComponent: DashboardComponentModel) => void;
  closeDialog: () => void;
};

const ComponentConfiguration = (props: ComponentConfigurationType) => {
  const { title: titleGiven, type: typeGiven, ...config } = props.component;

  const types = [
    { label: "Product Template Table", value: "ProductTemplateTable" },
    { label: "Dataset Line Chart", value: "DatasetLineChart" },
    { label: "Dataset Scatter Chart", value: "DatasetScatterChart" },
    { label: "Dataset Sankey Chart", value: "DatasetSankeyChart" },
    { label: "Dataset Table", value: "DatasetTable" },
    { label: "Dataset Pie Chart", value: "DatasetPieChart" },
    { label: "Free Text", value: "FreeText" },
    { label: "Failing component", value: "FailingComponentTest" },
  ];

  const monacoRef = useRef<Monaco | null>(null);

  const [title, setTitle] = useState(titleGiven || "");
  const [type, setType] = useState<any>(typeGiven);
  const [value, setValue] = useState<string | undefined>(JSON.stringify(config, null, 2));
  const [error, setError] = useState(false);

  useEffect(() => {
    return () => {
      monacoRef.current?.languages.json.jsonDefaults.setDiagnosticsOptions({});
    };
  }, []);

  const onEditorChange = (newValue: string | undefined) => {
    setValue(newValue);
  };

  const onEditorMounted = (editor: any, monaco: Monaco) => {
    monacoRef.current = monaco;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "https://json-schema.org/",
          fileMatch: ["*"],
          schema: schema,
        },
      ],
    });
  };

  const onSaveButtonClick = () => {
    const val = JSON.parse(value!!);
    props.onChange({
      title: title,
      type: type,
      ...val,
    });
    props.closeDialog();
  };

  return (
    <div className={"flex flex-column gap-2"}>
      <div className={"flex gap-2"}>
        <div className={"flex flex-column gap-1"}>
          <b>Title</b>
          <InputText value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        <div className={"flex flex-column gap-1"}>
          <b>Type</b>
          <Dropdown
            value={type}
            options={types}
            onChange={(e: { value: any }) => setType(e.value)}
            placeholder="Select a type"
          />
        </div>
      </div>

      <Editor
        height="500px"
        key={"component-editor"}
        defaultLanguage="json"
        value={value}
        onChange={onEditorChange}
        onMount={onEditorMounted}
        onValidate={validated => setError(!!validated.length)}
        theme="vs-dark"
      />

      <div className={"flex justify-content-end"}>
        <Button
          icon={"pi pi-save"}
          label={"Save"}
          onClick={() => onSaveButtonClick()}
          disabled={error}
        />
      </div>
    </div>
  );
};

type GroupLayoutConfigurationType = {
  layout: DashboardComponentGroupLayout;
  onChange: (newGroup: DashboardComponentGroupLayout) => void;
  closeDialog: () => void;
};

const GroupLayoutConfiguration = (props: GroupLayoutConfigurationType) => {
  const [type, setType] = useState<DashboardLayoutType | undefined>(props.layout.type);

  const [gap, setGap] = useState<number | null>(props.layout.gap || 2);
  const [justify, setJustify] = useState<DashboardComponentJustify>(
    props.layout.justify || DashboardComponentJustify.BETWEEN
  );
  const [align, setAlign] = useState<DashboardComponentAlign>(
    props.layout.align || DashboardComponentAlign.CENTER
  );

  const typeOptions = Object.values(DashboardLayoutType).map(type => ({
    label: type,
    value: type,
  }));

  const justifyOptions = Object.values(DashboardComponentJustify).map(type => ({
    label: type,
    value: type,
  }));

  const alignOptions = Object.values(DashboardComponentAlign).map(type => ({
    label: type,
    value: type,
  }));

  const onSaveClick = () => {
    const layout: DashboardComponentGroupLayout = {
      type: type || DashboardLayoutType.VERTICAL,
    };
    if (type === DashboardLayoutType.HORIZONTAL_FLEX) {
      if (align) {
        layout.align = align;
      }
      if (gap != null) {
        layout.gap = gap;
      }

      if (justify) {
        layout.justify = justify;
      }
    }

    props.onChange(layout);
    props.closeDialog();
  };

  return (
    <div className={"flex flex-column gap-2"}>
      <div className={"flex flex-column gap-1"}>
        <b>Type</b>
        <Dropdown
          value={type}
          options={typeOptions}
          onChange={(e: { value: DashboardLayoutType }) => setType(e.value)}
          placeholder="Select a type"
        />
      </div>

      {type === DashboardLayoutType.HORIZONTAL_FLEX && (
        <>
          <div className={"flex flex-column gap-1"}>
            <b>Gap</b>
            <InputNumber
              value={gap}
              onChange={e => setGap(e.value)}
              min={0}
              max={8}
              maxFractionDigits={0}
            />
          </div>

          <div className="flex flex-column gap-1">
            <b>Justify</b>
            <Dropdown
              value={justify}
              options={justifyOptions}
              onChange={(e: { value: DashboardComponentJustify }) => setJustify(e.value)}
              placeholder="Select a justify"
            />
          </div>

          <div className={"flex flex-column gap-1"}>
            <b>Align</b>
            <Dropdown
              value={align}
              options={alignOptions}
              onChange={(e: { value: DashboardComponentAlign }) => setAlign(e.value)}
              placeholder="Select a align"
            />
          </div>
        </>
      )}

      <div className={"flex justify-content-end"}>
        <Button icon={"pi pi-save"} label={"Save"} onClick={() => onSaveClick()} />
      </div>
    </div>
  );
};

export default DashboardTabsSubForm;
